import React, { useState, useEffect } from 'react';
import { Card, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Question, QuestionResponse, Scores } from "@/types/test-response";
import { MonitoringEvent } from "@/types/monitoring";
import { cn } from "@/lib/utils";
import { AlertTriangle, Check, Clock, Loader2, ChevronDown, MessageCircle, X, Code2 } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { ScoreInput } from "@/components/test-details/ScoreInput";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScoreDisplay } from "../responses-table/ScoreDisplay";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { generateSecureFileUrl } from "@/utils/secure-storage";
import { extractJsonbUrl } from "@/utils/mediaResponseUtils";
import { EnhancedMediaPlayer } from "./EnhancedMediaPlayer";
import Prism from "prismjs";
import "prismjs/themes/prism.css";
interface QuestionResponseCardProps {
  question: Question;
  questionResponse?: QuestionResponse;
  questionNumber: number;
  isEditing: boolean;
  score: number;
  onScoreChange: (score: number) => void;
  monitoringEvents?: MonitoringEvent[];
}
export function QuestionResponseCard({
  question,
  questionResponse,
  questionNumber,
  isEditing,
  score,
  onScoreChange,
  monitoringEvents = []
}: QuestionResponseCardProps) {
  const [isAIFeedbackOpen, setIsAIFeedbackOpen] = useState(false);
  const [isRetryingTranscription, setIsRetryingTranscription] = useState(false);
  const [secureMediaUrl, setSecureMediaUrl] = useState<string | null>(null);
  const { toast } = useToast();

  // Use a more robust score handler that prevents invalid values
  const handleScoreChange = (newScore: number) => {
    // Validate score is within valid range for this question
    const validScore = Math.min(Math.max(0, newScore), question.marks);
    onScoreChange(validScore);
  };

  // Generate secure media URL for private storage paths (only for audio/video)
  useEffect(() => {
    const isMediaQuestion = ["audio_response", "video_response"].includes(question.question_type);
    if (!isMediaQuestion) { setSecureMediaUrl(null); return; }

    const buildUrl = async () => {
      if (!questionResponse?.response_content) { setSecureMediaUrl(null); return; }
      try {
        const mediaUrl = extractJsonbUrl(questionResponse.response_content);
        console.log("[Media] raw response_content:", questionResponse.response_content);
        console.log("[Media] extracted mediaUrl:", mediaUrl);
        if (!mediaUrl) { setSecureMediaUrl(null); return; }
        if (!/^https?:\/\//i.test(mediaUrl)) {
          const url = await generateSecureFileUrl('test-responses', mediaUrl);
          console.log('[Media] generated signed URL:', url);
          setSecureMediaUrl(url);
          return;
        }
        // If already a proxy URL, rebuild to refresh token
        const u = new URL(mediaUrl);
        if (u.pathname.includes('/functions/v1/webcam-proxy')) {
          const bucket = u.searchParams.get('bucket') || 'test-responses';
          const filePath = u.searchParams.get('file_path');
          const url = filePath ? await generateSecureFileUrl(bucket, filePath) : mediaUrl;
          setSecureMediaUrl(url);
          return;
        }
        // If public test-responses path, convert to secure
        if (u.pathname.includes('/storage/v1/object/public/test-responses/')) {
          const filePath = u.pathname.split('/test-responses/')[1];
          const url = filePath ? await generateSecureFileUrl('test-responses', filePath) : mediaUrl;
          setSecureMediaUrl(url);
          return;
        }
        setSecureMediaUrl(mediaUrl);
      } catch (e) {
        console.warn('Failed to build secure media URL', e);
        setSecureMediaUrl(null);
      }
    };
    buildUrl();
  }, [question.question_type, questionResponse?.response_content]);

  const handleRetryTranscription = async () => {
    if (!questionResponse || isRetryingTranscription) return;
    try {
      setIsRetryingTranscription(true);
      toast({ title: 'Retrying transcription', description: 'This may take a few moments...' });
      const { error } = await supabase.functions.invoke('transcribe-media', { body: { questionResponseId: questionResponse.id } });
      if (error) throw error;
    } catch (err: any) {
      toast({ title: 'Error', description: 'Failed to retry transcription: ' + (err.message || 'Unknown error'), variant: 'destructive' });
    } finally {
      setIsRetryingTranscription(false);
    }
  };

  // Helper function to safely parse JSON or return a default value
  const safelyParseJson = (jsonString: string | any, defaultValue: any = {}): any => {
    if (typeof jsonString !== 'string') {
      return jsonString || defaultValue;
    }
    try {
      // First check if the string looks like JSON (starts with { or [])
      const trimmed = jsonString.trim();
      if (trimmed.startsWith('{') && trimmed.endsWith('}') || trimmed.startsWith('[') && trimmed.endsWith(']')) {
        return JSON.parse(jsonString);
      }
      // If it doesn't look like JSON, return the string as is
      return jsonString;
    } catch (error) {
      console.error("Error parsing JSON:", error, "Content:", jsonString);
      return defaultValue;
    }
  };
  const renderQuestionContent = () => {
    const content = safelyParseJson(question.content);
    return <div className="mb-6">
        <p className="text-xl font-medium text-primary">{typeof content === 'object' && content?.question ? content.question : 'No question text available'}</p>
      </div>;
  };
  const renderResponseContent = () => {
    if (!questionResponse) {
      return <Alert variant="destructive" className="mt-2">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>No response recorded</AlertDescription>
        </Alert>;
    }
    const content = questionResponse.response_content;
    if (question.question_type === "multiple_choice") {
      const questionContent = safelyParseJson(question.content);
      const responseContent = safelyParseJson(content);

      // Parse the selected option from the response
      let selectedOption = null;
      if (typeof responseContent === 'string') {
        // If response is just a string, it's the selected option
        selectedOption = responseContent;
      } else if (responseContent && typeof responseContent === "object") {
        // If response is an object, check for a 'selected' property
        selectedOption = responseContent.selected || responseContent;
      }

      // For debugging
      console.log("MCQ Response:", {
        responseContent,
        selectedOption,
        options: questionContent?.options
      });
      return <div className="mt-4 space-y-2">
          {questionContent && questionContent.options && Array.isArray(questionContent.options) ? questionContent.options.map((option: string, index: number) => {
          // Determine if this option is the selected one and if it's correct
          const isSelected = selectedOption === option || selectedOption === index;
          const isCorrect = index === questionContent.correctOption;
          
          return <div key={index} className={cn(
            "p-2 border rounded-md flex items-center gap-2",
            isCorrect && "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800",
            isSelected && !isCorrect && "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800",
            !isCorrect && !isSelected && "bg-card"
          )}>
                  <span className="text-sm flex-1">{option}</span>
                  {isCorrect && (
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                  )}
                  {isSelected && !isCorrect && (
                    <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                  )}
                </div>;
        }) : <Alert>
                <AlertDescription>No options available for this question</AlertDescription>
              </Alert>}
        </div>;
    } else if (question.question_type === "essay") {
      let essayText = '';

      // Handle different response formats for essay questions
      try {
        if (typeof content === 'string') {
          // If it looks like JSON, try to parse it
          if (content.trim().startsWith('{') && content.trim().endsWith('}') || content.trim().startsWith('[') && content.trim().endsWith(']')) {
            const parsed = JSON.parse(content);
            essayText = typeof parsed === 'object' && parsed !== null && 'text' in parsed ? String(parsed.text) : JSON.stringify(parsed);
          } else {
            // If it's not JSON formatted, use it directly
            essayText = content;
          }
        } else if (content && typeof content === 'object') {
          // Handle object response - convert text to string to prevent type errors
          essayText = 'text' in content ? String(content.text) : JSON.stringify(content);
        }
      } catch (error) {
        console.error("Error handling essay response:", error);
        essayText = String(content || '');
      }
      return <div className="mt-6">
          <div className="bg-card border rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-200">
            <div className="whitespace-pre-wrap text-base leading-relaxed text-foreground">{essayText}</div>
          </div>
        </div>;
    } else if (question.question_type === "audio_response" || question.question_type === "video_response") {
      const rawUrl = extractJsonbUrl(questionResponse.response_content);
      return (
        <div className="mt-6 space-y-5">
          {secureMediaUrl ? (
            <EnhancedMediaPlayer 
              src={secureMediaUrl} 
              type={question.question_type === "video_response" ? "video" : "audio"}
            />
          ) : (
            <div className="bg-muted/20 p-4 rounded-md shadow-sm flex items-center justify-center h-32">
              <p className="text-muted-foreground">
                {questionResponse?.transcribed_content ? 
                  "Video upload unsuccessful from candidate side, audio transcription available" : 
                  (rawUrl ? "Unable to load media" : "No media available")
                }
              </p>
            </div>
          )}

          {questionResponse.transcribed_content ? (
            <div>
              <p className="text-sm font-medium mb-3 text-primary">Transcription:</p>
              <ScrollArea className="h-[180px] w-full border rounded-md p-5 bg-muted/20 shadow-sm">
                <div className="whitespace-pre-wrap text-base leading-relaxed">{questionResponse.transcribed_content}</div>
              </ScrollArea>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm ml-2 flex items-center justify-between w-full">
                <span>No transcription available</span>
                <Button variant="outline" size="sm" onClick={handleRetryTranscription} disabled={isRetryingTranscription}>
                  {isRetryingTranscription ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Retry Transcription
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {questionResponse.transcription_status === "failed" && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Transcription failed: {questionResponse.transcription_error || "Unknown error"}
              </AlertDescription>
            </Alert>
          )}
        </div>
      );
    } else if (question.question_type === "coding") {
      const responseContent = safelyParseJson(content);
      
      // Extract code and language from response
      let code = '';
      let language = 'javascript'; // default fallback
      
      if (typeof responseContent === 'object' && responseContent !== null) {
        code = responseContent.code || '';
        language = responseContent.language || 'javascript';
      } else if (typeof responseContent === 'string') {
        code = responseContent;
      }

      // Load and apply syntax highlighting
      const highlightCode = (code: string, language: string) => {
        try {
          // Ensure Prism has the language grammar
          if (Prism.languages[language]) {
            return Prism.highlight(code, Prism.languages[language], language);
          } else {
            // Fallback to plain text if language not supported
            return code;
          }
        } catch (error) {
          console.warn(`Failed to highlight code for language ${language}:`, error);
          return code;
        }
      };

      return (
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2 mb-3">
            <Code2 className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-primary">Code Response</span>
            <Badge variant="secondary" className="text-xs">
              {language}
            </Badge>
          </div>
          
          <div className="bg-card border rounded-lg shadow-sm overflow-hidden">
            <div className="bg-muted/30 px-4 py-2 border-b">
              <span className="text-xs text-muted-foreground font-mono">
                Submitted Solution
              </span>
            </div>
            <ScrollArea className="max-h-[400px]">
              <pre className="p-4 text-sm font-mono leading-relaxed overflow-x-auto">
                <code 
                  className={`language-${language}`}
                  dangerouslySetInnerHTML={{ 
                    __html: code ? highlightCode(code, language) : 'No code submitted' 
                  }}
                />
              </pre>
            </ScrollArea>
          </div>
          
          {!code && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>No code was submitted for this question</AlertDescription>
            </Alert>
          )}
        </div>
      );
    }
    return <Alert variant="destructive" className="mt-2">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Unsupported question type</AlertDescription>
      </Alert>;
  };

  const renderAIFeedback = () => {
    if (!questionResponse?.ai_feedback) return null;
    const feedback = questionResponse.ai_feedback;
    return <div className="mt-6 pt-5 border-t">
        <Button variant="outline" onClick={() => setIsAIFeedbackOpen(!isAIFeedbackOpen)} className="flex items-center justify-between w-full mb-4 bg-white">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 bg-yellow-400 rounded-full flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-medium">AI Feedback</span>
          </div>
          <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isAIFeedbackOpen && "rotate-180")} />
        </Button>
        
        {isAIFeedbackOpen && <div className="space-y-4">
            <p className="text-sm text-muted-foreground bg-muted/20 p-4 rounded-md shadow-sm">{feedback.overall_feedback}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {feedback.strengths && feedback.strengths.length > 0 && <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-900/30 p-4 rounded-md shadow-sm">
                  <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">Strengths:</p>
                  <ul className="list-disc list-inside text-xs text-green-800 dark:text-green-300 space-y-1.5">
                    {feedback.strengths.map((strength, index) => <li key={index} className="leading-relaxed">{strength}</li>)}
                  </ul>
                </div>}
              
              {feedback.improvements && feedback.improvements.length > 0 && <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 p-4 rounded-md shadow-sm">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">Areas for Improvement:</p>
                  <ul className="list-disc list-inside text-xs text-amber-800 dark:text-amber-300 space-y-1.5">
                    {feedback.improvements.map((improvement, index) => <li key={index} className="leading-relaxed">{improvement}</li>)}
                  </ul>
                </div>}
            </div>
            
            {/* New criteria-specific scores section */}
            {feedback.criteria_scores && Object.keys(feedback.criteria_scores).length > 0 && <div className="mt-5 pt-4 border-t border-dashed">
                <p className="text-sm font-medium mb-3 text-primary">Criteria Breakdown</p>
                <div className="bg-muted/10 rounded-md shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/30">
                          <th className="text-left py-2 px-4 font-medium text-muted-foreground">Criteria</th>
                          <th className="text-center py-2 px-4 font-medium text-muted-foreground w-24">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(feedback.criteria_scores).map(([criteriaName, criteriaScore], index) => {
                  const reviewPoint = feedback.review_points?.[criteriaName] || null;
                    
                    // Find the max score from the question's markscheme
                    const maxScore = question?.markscheme?.criteria?.find(
                      (c) => c.name === criteriaName
                    )?.max_points || 1;
                    
                    return <tr key={criteriaName} className={index % 2 === 0 ? 'bg-muted/5' : 'bg-transparent'}>
                              <td className="py-3 px-4">
                                <p className="font-medium text-sm">{criteriaName}</p>
                                {reviewPoint && <p className="text-xs text-muted-foreground mt-1">{reviewPoint}</p>}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <Badge variant={Number(criteriaScore) > 0 ? "default" : "outline"} className="inline-flex justify-center min-w-[3rem]">
                                  {String(criteriaScore)}/{maxScore}
                                </Badge>
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>}
          </div>}
      </div>;
  };
  const renderMonitoringEvents = () => {
    if (!monitoringEvents || monitoringEvents.length === 0) return null;
    return <div className="mt-6 pt-5 border-t">
        <p className="text-sm font-medium mb-3 text-primary">Monitoring Events ({monitoringEvents.length})</p>
        <div className="bg-muted/20 p-4 rounded-md shadow-sm space-y-2.5">
          {monitoringEvents.slice(0, 3).map((event, index) => <div key={index} className="text-xs flex items-center gap-3">
              <Badge variant="outline" className="text-xs font-normal">
                {event.event_type}
              </Badge>
              <span className="text-muted-foreground">{new Date(event.created_at).toLocaleTimeString()}</span>
            </div>)}
          {monitoringEvents.length > 3 && <p className="text-xs text-muted-foreground pt-1 border-t border-dashed">
              + {monitoringEvents.length - 3} more events
            </p>}
        </div>
      </div>;
  };
  const getEvaluationStatus = () => {
    if (!questionResponse?.evaluation_status) {
      return null;
    }
    const status = questionResponse.evaluation_status;
    const statusConfig = {
      "pending": {
        text: "Pending",
        icon: Clock,
        variant: "secondary" as const
      },
      "in_progress": {
        text: "Evaluating",
        icon: Loader2,
        variant: "secondary" as const
      },
      "completed": {
        text: "Evaluated",
        icon: Check,
        variant: "default" as const
      },
      "failed": {
        text: "Failed",
        icon: AlertTriangle,
        variant: "destructive" as const
      }
    };
    return statusConfig[status] || null;
  };
  const evaluationStatus = getEvaluationStatus();

  // Get the AI score from the question response if available
  const aiScore = questionResponse?.ai_feedback?.score || 0;
  return <Card className="overflow-hidden shadow-sm border-slate-200 dark:border-slate-800">
      <CardHeader className="bg-muted/50 py-4 px-5">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="font-medium text-primary">Question {questionNumber}</span>
            <Badge variant="outline" className="ml-2 font-normal capitalize">
              {question.question_type.replace(/_/g, ' ')}
            </Badge>
            {evaluationStatus && <Badge variant={evaluationStatus.variant} className="ml-2">
                <evaluationStatus.icon className="h-3 w-3 mr-1" />
                {evaluationStatus.text}
              </Badge>}
          </div>
          <div className="flex items-center gap-4">
            {/* AI Score display */}
            {questionResponse?.ai_feedback && <div className="flex flex-col items-end">
                <span className="text-xs text-muted-foreground mb-1">AI Score</span>
                <ScoreDisplay score={aiScore} total={question.marks} className="text-sm" />
              </div>}
            
            {/* User Score input/display */}
            <div className="flex flex-col items-end">
              <span className="text-xs text-muted-foreground mb-1">Your Score</span>
              {isEditing ? <ScoreInput value={score} onChange={handleScoreChange} max={question.marks} /> : <Badge variant={score > 0 ? "default" : "outline"} className="py-1.5">
                  {score} / {question.marks}
                </Badge>}
            </div>
          </div>
        </div>
      </CardHeader>
      <div className="p-5">
        {renderQuestionContent()}
        <Separator className="my-5" />
        {renderResponseContent()}
        {renderAIFeedback && renderAIFeedback()}
        {renderMonitoringEvents && renderMonitoringEvents()}
      </div>
    </Card>;
}